import * as React from 'react';
import { cn } from '@/lib/utils';

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired once all slots are filled (typing, paste or SMS/password-manager autofill). */
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Marks the slots as invalid; flip `errorKey` to replay the shake. */
  invalid?: boolean;
  errorKey?: number;
  id?: string;
  'aria-label'?: string;
  className?: string;
}

/**
 * Digit-slot one-time-code input. A single transparent native input sits on
 * top of the visual slots, so paste, `autocomplete="one-time-code"`, password
 * managers and mobile numeric keyboards all work without per-box focus juggling.
 */
export const OtpInput = React.forwardRef<HTMLInputElement, OtpInputProps>(
  (
    {
      value,
      onChange,
      onComplete,
      length = 6,
      disabled,
      autoFocus,
      invalid,
      errorKey,
      id,
      'aria-label': ariaLabel = 'Bestätigungscode',
      className,
    },
    ref,
  ) => {
    const [focused, setFocused] = React.useState(false);
    const [shake, setShake] = React.useState(false);
    const half = Math.ceil(length / 2);

    React.useEffect(() => {
      if (!errorKey) return;
      setShake(true);
      const t = setTimeout(() => setShake(false), 400);
      return () => clearTimeout(t);
    }, [errorKey]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value.replace(/\D/g, '').slice(0, length);
      if (next === value) return;
      onChange(next);
      if (next.length === length) onComplete?.(next);
    };

    const activeIndex = Math.min(value.length, length - 1);

    const renderSlot = (i: number) => {
      const char = value[i];
      const isActive = focused && !disabled && i === activeIndex;
      return (
        <div
          key={i}
          className={cn(
            'relative flex h-12 w-9 items-center justify-center rounded-lg border bg-background font-mono text-xl font-medium tabular-nums text-foreground transition-[border-color,box-shadow] duration-150 sm:w-10',
            invalid ? 'border-danger' : char ? 'border-foreground/25' : 'border-border',
            isActive && !invalid && 'border-accent ring-[3px] ring-accent/20',
            isActive && invalid && 'ring-[3px] ring-danger/20',
            disabled && 'opacity-60',
          )}
        >
          {char ?? (isActive && <span className="otp-caret h-5 w-px bg-foreground" />)}
        </div>
      );
    };

    return (
      <div className={cn('relative mx-auto w-fit', shake && 'otp-shake', className)}>
        <div className="flex items-center gap-1.5 sm:gap-2" aria-hidden="true">
          {Array.from({ length: half }, (_, i) => renderSlot(i))}
          <span className="h-px w-2.5 bg-border" />
          {Array.from({ length: length - half }, (_, i) => renderSlot(i + half))}
        </div>
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          maxLength={length}
          value={value}
          onChange={handleChange}
          onFocus={(e) => {
            setFocused(true);
            const end = e.currentTarget.value.length;
            e.currentTarget.setSelectionRange(end, end);
          }}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          spellCheck={false}
          className="absolute inset-0 h-full w-full cursor-text bg-transparent text-transparent caret-transparent opacity-0 outline-none selection:bg-transparent disabled:cursor-not-allowed"
        />
      </div>
    );
  },
);
OtpInput.displayName = 'OtpInput';
